import type { FC, ReactNode } from 'react'
import type { Door } from '@/types'

import { useState } from 'react'
import Image from 'next/image'
import { Check, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatMoney, formatRange } from '@/lib/format'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ButtonLink } from '@/components/generals/button'
import { Enter, HeroTitle } from '@/components/motion/hero-title'
import { useQuoteList } from '@/components/generals/quote-list-provider'
import { doorQuoteItem } from '@/components/widgets/door-card'
import { doors } from '@/constants/doors'

const SHOWCASE_IDS = ['ridgeline-classic', 'hartwell-carriage', 'norrland-flush', 'clearview-pane']

const showcase: Door[] = SHOWCASE_IDS
    .map(id => doors.find(door => door.id === id))
    .filter((door): door is Door => Boolean(door))

const lowestPrice = Math.min(...doors.map(door => door.priceFrom))
const bestRValue = Math.max(...doors.map(door => Number.parseInt(door.rValue.replace(/\D/g, ''), 10) || 0))

const stats = [
    { value: String(doors.length), label: "Door models" },
    { value: formatMoney(lowestPrice), label: "Installed from" },
    { value: `R-${bestRValue}`, label: "Best insulation" }
]

interface DoorsHeroProps {
    galleryHref: string
    helpHref: string
}

export const DoorsHero: FC<DoorsHeroProps> = ({ galleryHref, helpHref }): ReactNode => {
    const [active, setActive] = useState(0)
    const [previous, setPrevious] = useState<number | null>(null)
    const [paused, setPaused] = useState(false)
    const { has, toggle } = useQuoteList()

    const door = showcase[active]
    const item = door ? doorQuoteItem(door, door.colors[0]) : null
    const saved = item ? has(item.key) : false

    const show = (index: number) => {
        if (index === active) return

        setPrevious(active)
        setActive(index)
    }

    const advance = () => show((active + 1) % showcase.length)

    return (
        <section id='doors-hero' className='relative overflow-hidden pb-16 pt-36 md:pb-24 md:pt-44'>
            <div aria-hidden='true' className='pointer-events-none absolute -left-40 top-20 size-[38rem] rounded-full bg-ember/[0.07] blur-3xl' />

            <Container className='relative grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-10'>
                <div className='lg:col-span-6'>
                    <Enter delay={60}>
                        <Eyebrow>New garage doors</Eyebrow>
                    </Enter>

                    <HeroTitle
                        text="A new door changes how your home meets the street."
                        accent={['meets', 'the', 'street.']}
                        className='mt-6 max-w-[14ch] text-h1 font-semibold text-ink'
                    />

                    <Enter delay={420} className='mt-7 max-w-lg text-lead text-pretty text-slate'>
                        On many homes the garage door is 30% of the front. Browse the styles we install, price the one you like, and shortlist up to 4 to compare side by side.
                    </Enter>

                    <Enter delay={540} className='mt-10 flex flex-wrap items-center gap-3'>
                        <ButtonLink href={galleryHref} size='lg'>See the gallery</ButtonLink>
                        <ButtonLink href={helpHref} variant='outline' size='lg'>Help me pick a door</ButtonLink>
                    </Enter>

                    <Enter delay={660} className='mt-12'>
                        <dl className='grid max-w-lg grid-cols-3 divide-x divide-ink/10 border-y border-ink/10'>
                            {stats.map(stat => (
                                <div key={stat.label} className='px-4 py-5 first:pl-0'>
                                    <dt className='font-mono text-[10.5px] uppercase tracking-[0.16em] text-slate'>{stat.label}</dt>
                                    <dd className='mt-2 font-heading text-[1.6rem] font-semibold leading-none tracking-[-0.04em] text-ink sm:text-[1.9rem]'>{stat.value}</dd>
                                </div>
                            ))}
                        </dl>
                    </Enter>
                </div>

                <Enter delay={300} className='lg:col-span-6'>
                    <div
                        className='relative'
                        onMouseEnter={() => setPaused(true)}
                        onMouseLeave={() => setPaused(false)}
                    >
                        <div className='relative aspect-[4/5] overflow-hidden rounded-[32px] bg-ink sm:aspect-[5/5] lg:aspect-[5/6]'>
                            {showcase.map((entry, index) => {
                                const isActive = index === active
                                const isPrevious = index === previous

                                return (
                                    <div
                                        key={entry.id}
                                        aria-hidden={!isActive}
                                        className={cn(
                                            'absolute inset-0',
                                            isActive && 'z-20 transition-[clip-path] duration-[1100ms] ease-door [clip-path:inset(0_0_0_0)]',
                                            isPrevious && 'z-10 [clip-path:inset(0_0_0_0)]',
                                            !isActive && !isPrevious && 'z-0 [clip-path:inset(0_0_100%_0)]'
                                        )}
                                    >
                                        <Image
                                            src={entry.image}
                                            alt={isActive ? `${entry.name} garage door in ${entry.colors[0]}` : ''}
                                            fill
                                            priority={index === 0}
                                            sizes='(min-width: 1024px) 45vw, 100vw'
                                            className={cn('object-cover transition-transform duration-[2400ms] ease-expo', isActive ? 'scale-100' : 'scale-[1.08]')}
                                        />
                                    </div>
                                )
                            })}

                            <div aria-hidden='true' className='pointer-events-none absolute inset-0 z-30 bg-gradient-to-t from-ink/85 via-ink/10 to-ink/30' />

                            <div className='absolute inset-x-5 top-5 z-40 flex items-start justify-between gap-3'>
                                <span className='rounded-full bg-cream/90 px-3.5 py-2 font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink backdrop-blur-md'>
                                    {door?.style} · {door?.material}
                                </span>

                                <span className='rounded-full bg-ink/50 px-3 py-2 font-mono text-[11px] text-cream ring-1 ring-cream/15 backdrop-blur-md'>
                                    {String(active + 1).padStart(2, '0')} / {String(showcase.length).padStart(2, '0')}
                                </span>
                            </div>

                            {door && item && (
                                <div className='absolute inset-x-5 bottom-5 z-40 flex flex-wrap items-end justify-between gap-4 text-cream sm:inset-x-7 sm:bottom-7'>
                                    <div key={door.id} className='animate-fade-up'>
                                        <p className='font-heading text-[1.9rem] font-semibold leading-none tracking-[-0.04em] sm:text-[2.4rem]'>{door.name}</p>
                                        <p className='mt-2 font-mono text-[13px] text-cream/75'>
                                            {formatRange(door.priceFrom, door.priceTo)} · Insulation {door.rValue}
                                        </p>
                                    </div>

                                    <button
                                        type='button'
                                        onClick={() => toggle(item)}
                                        aria-pressed={saved}
                                        className={cn(
                                            'inline-flex h-11 items-center gap-2 rounded-full px-4 text-[13.5px] font-medium transition-colors duration-300',
                                            saved ? 'bg-ember text-white hover:bg-ember-deep' : 'bg-cream text-ink hover:bg-white'
                                        )}
                                    >
                                        {saved ? <Check className='size-4' aria-hidden='true' /> : <Plus className='size-4' aria-hidden='true' />}
                                        {saved ? "Saved to your quote" : "Save to your quote"}
                                    </button>
                                </div>
                            )}
                        </div>

                        <div role='tablist' aria-label="Door styles" className='mt-4 grid grid-cols-4 gap-2'>
                            {showcase.map((entry, index) => {
                                const isActive = index === active

                                return (
                                    <button
                                        key={entry.id}
                                        type='button'
                                        role='tab'
                                        aria-selected={isActive}
                                        onClick={() => show(index)}
                                        className={cn(
                                            'group relative overflow-hidden rounded-2xl border px-3 pb-3 pt-3.5 text-left transition-colors duration-300',
                                            isActive ? 'border-ink/15 bg-card' : 'border-transparent hover:bg-card/70'
                                        )}
                                    >
                                        <span className={cn('block truncate text-[12.5px] font-medium sm:text-[13.5px]', isActive ? 'text-ink' : 'text-slate group-hover:text-ink')}>
                                            {entry.style}
                                        </span>

                                        <span className='mt-2.5 block h-[3px] overflow-hidden rounded-full bg-ink/10'>
                                            {isActive && (
                                                <span
                                                    key={`${entry.id}-${active}`}
                                                    onAnimationEnd={advance}
                                                    className='block h-full origin-left rounded-full bg-ember animate-door-progress motion-reduce:animate-none'
                                                    style={{ animationPlayState: paused ? 'paused' : 'running' }}
                                                />
                                            )}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>
                    </div>
                </Enter>
            </Container>
        </section>
    )
}
